import { useEffect, useState, useMemo } from "react";
import { Link } from "@/lib/router-compat";
import { allBlogPosts } from "@/lib/allBlogPosts";
import type { BlogPost } from "@/data/blogPosts";
import { deduplicatePosts, sortByDateDesc } from "@/lib/blogPostProcessor";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CustomCursor } from "@/components/CustomCursor";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { AVATAR_WEBP } from "@/lib/optimizedImages";
import {
  normalizePostDates,
  formatDisplayDate,
  getNormalizedDate,
} from "@/lib/dateNormalizer";
import {
  ArrowRight,
  Clock,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Calendar,
  Sparkles,
  TrendingUp,
  Flame,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// SITE RULE: most-recent-first ordering (newest article on top)
const dedupedPosts = sortByDateDesc(deduplicatePosts(allBlogPosts));
const blogDateMap = normalizePostDates(dedupedPosts);

const ITEMS_PER_PAGE = 6;

// High quality, thematic editorial fallback images matching RevOps / Architecture / Systems
const EDITORIAL_COVERS = [
  "https://vibe.filesafe.space/1790273900671562476/assets/4e567e59-bfc8-4f45-a70c-fe9fc7b99c9c.jpg",
  "https://vibe.filesafe.space/1790273900671562476/assets/fcfc42ef-6fd7-4851-a337-0e558a1b8b9f.jpg",
  "https://vibe.filesafe.space/1790273900671562476/assets/716c93c6-af3d-445f-aa88-ac906a6f3633.jpg",
  "https://vibe.filesafe.space/1790273900671562476/assets/0790271b-e483-4e16-b6be-ac0bdbd01116.jpg",
];

function getCoverImage(index: number): string {
  return EDITORIAL_COVERS[index % EDITORIAL_COVERS.length];
}

// Collapsible Inline FAQ item inside post card
function CardFaqAccordion({
  faq,
}: {
  faq: { question: string; answer: string };
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-[#0B7CF2]/10 rounded-xl overflow-hidden bg-white/70 transition-colors">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="w-full py-2.5 px-3 flex items-center justify-between text-left gap-2 hover:bg-[#F5FAFF] transition-colors"
      >
        <span className="text-[12px] font-semibold text-[#071E61] line-clamp-1">
          {faq.question}
        </span>
        <span
          className={`shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[#0B7CF2] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </span>
      </button>
      {isOpen && (
        <div
          className="px-3 pb-3 pt-1 text-[11px] text-[#536B89] leading-relaxed border-t border-[#0B7CF2]/10 bg-[#F8FCFF]"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {faq.answer}
        </div>
      )}
    </div>
  );
}

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [auditEmail, setAuditEmail] = useState("");
  const { toast } = useToast();

  // Ordered, curated categories with counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    dedupedPosts.forEach((post) => {
      counts[post.category] = (counts[post.category] || 0) + 1;
    });
    return counts;
  }, []);

  const categories = useMemo(() => {
    // Curated order from broad foundational architecture to specialized operations
    const ORDER = [
      "Revenue Operations",
      "Systems Architecture",
      "Automation Strategy",
      "Artificial Intelligence",
      "Hiring Strategy",
      "Agency Scaling",
      "Compensation & Logistics",
    ];

    const orderedItems = ORDER.filter((name) => categoryCounts[name]).map(
      (name) => ({
        name,
        count: categoryCounts[name] || 0,
      }),
    );

    return [{ name: "All", count: dedupedPosts.length }, ...orderedItems];
  }, [categoryCounts]);

  // Filtered posts based on search & category
  const filteredPosts = useMemo(() => {
    return dedupedPosts.filter((post) => {
      const matchesCategory =
        selectedCategory === "All" || post.category === selectedCategory;
      const matchesSearch =
        !searchQuery.trim() ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.tags?.some((t) =>
          t.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Top ranking articles for the right sidebar (sorted by length/prominence)
  const topRankingArticles = useMemo(() => {
    return dedupedPosts.slice(0, 8);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const totalPages = Math.ceil(filteredPosts.length / ITEMS_PER_PAGE);
  const paginatedPosts = filteredPosts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleAuditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!auditEmail || !auditEmail.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid work email address.",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Audit Request Received",
      description: "We'll send your custom systems delegation audit shortly.",
    });
    setAuditEmail("");
  };

  return (
    <div className="min-h-screen bg-[#FBFDFF] text-[#071E61] selection:bg-[#0B7CF2]/20 relative overflow-hidden font-sans">
      <CustomCursor />
      <Navbar />
      <MobileBottomBar />

      <main className="pt-32 md:pt-36 pb-24 px-4 sm:px-6 md:px-10 lg:px-12 max-w-[1520px] mx-auto relative z-10">
        {/* =========================================================
            HEADER BAR: "All articles" + Live Search Bar on the Right
            ========================================================= */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-[44px] font-heading font-black tracking-[-0.04em] text-[#071E61]">
              All articles
            </h1>
            <p className="text-sm sm:text-base text-[#536B89] mt-1">
              Browse every production architecture, workflow blueprint, and
              framework.
            </p>
          </div>

          {/* Search Input on the right */}
          <div className="w-full md:w-[380px] lg:w-[440px] relative">
            <Search className="w-4 h-4 text-[#6A7D97] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder="Search title, body, FAQs, keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-11 bg-white border border-slate-200 rounded-full pl-11 pr-4 text-xs sm:text-sm text-[#071E61] placeholder:text-[#8A9DB5] focus:outline-none focus:border-[#0B7CF2] focus:ring-2 focus:ring-[#0B7CF2]/15 transition-all shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 hover:text-slate-600"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* =========================================================
            MAIN CONTENT AREA: 2-Column Grid (Left) + Sidebar (Right)
            ========================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_390px] gap-8 xl:gap-10 items-start">
          {/* =========================================================
              LEFT COLUMN: Filter Pills + 2-Column Cards Grid + Pagination
              ========================================================= */}
          <div>
            {/* =========================================================
                CATEGORY PILLS BAR: Contained inside the left column,
                directly above the article cards it filters.
                ========================================================= */}
            <div className="mb-8">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 p-2 sm:p-2.5 rounded-2xl bg-white/90 border border-slate-200/80 shadow-[0_4px_16px_rgba(8,42,102,0.03)] backdrop-blur-xs">
                {categories.map((cat) => {
                  const isSelected = selectedCategory === cat.name;
                  return (
                    <button
                      key={cat.name}
                      onClick={() => setSelectedCategory(cat.name)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 border cursor-pointer ${
                        isSelected
                          ? "bg-[#062447] text-white border-[#062447] shadow-sm ring-2 ring-[#0B7CF2]/20"
                          : "bg-white text-[#425E82] hover:text-[#071E61] hover:bg-[#F4F9FF] hover:border-[#0B7CF2]/30 border-slate-200/90 shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
                      }`}
                    >
                      <span>{cat.name}</span>
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md transition-colors ${
                          isSelected
                            ? "bg-white/20 text-white"
                            : "bg-slate-100 text-[#536B89]"
                        }`}
                      >
                        {cat.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            {paginatedPosts.length === 0 ? (
              <div className="py-24 text-center bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                <p className="text-lg font-bold text-[#071E61] mb-2">
                  No blueprints found
                </p>
                <p className="text-sm text-[#536B89] mb-6">
                  Try adjusting your search query or select another category
                  from the top pills.
                </p>
                <button
                  onClick={() => {
                    setSelectedCategory("All");
                    setSearchQuery("");
                  }}
                  className="px-6 py-2.5 rounded-full bg-[#0B7CF2] text-white text-xs font-bold shadow hover:bg-[#0867CA] transition-colors"
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {paginatedPosts.map((post, index) => {
                  const cover = getCoverImage(index);
                  const faqs =
                    post.faq && post.faq.length > 0 ? post.faq.slice(0, 3) : [];

                  return (
                    <article
                      key={post.id}
                      className="group bg-white rounded-[22px] border border-slate-200/90 overflow-hidden shadow-[0_8px_24px_rgba(8,42,102,0.06)] hover:shadow-[0_18px_48px_rgba(8,42,102,0.12)] hover:border-[#0B7CF2]/30 transition-all duration-300 flex flex-col"
                    >
                      {/* Image Header with Category Badge */}
                      <Link
                        to={`/blog/${post.slug}`}
                        className="relative block aspect-[16/9.5] overflow-hidden bg-slate-100"
                      >
                        <img
                          src={cover}
                          alt={post.title}
                          loading="lazy"
                          className="w-full h-full object-cover object-center group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                        />
                        {/* Overlay subtle gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/15 pointer-events-none" />

                        {/* Category Badge overlay on top-left of image */}
                        <div className="absolute top-3.5 left-3.5 z-10">
                          <span className="inline-flex items-center px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider bg-black/75 text-white backdrop-blur-md border border-white/15">
                            {post.category}
                          </span>
                        </div>
                      </Link>

                      {/* Card Body */}
                      <div className="p-5 sm:p-6 flex-1 flex flex-col">
                        {/* Meta: Read time + Date */}
                        <div className="flex items-center gap-3 text-[11px] font-medium text-[#6A7D97] mb-3">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-[#0B7CF2]" />
                            {post.readTime}
                          </span>
                          <span className="text-slate-300">•</span>
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-[#0B7CF2]" />
                            {formatDisplayDate(
                              getNormalizedDate(post, blogDateMap),
                            )}
                          </span>
                        </div>

                        {/* Author/Architect Line */}
                        <div className="flex items-center gap-2 mb-3">
                          <img
                            src={AVATAR_WEBP}
                            alt="Marjohn Robillo"
                            width={24}
                            height={24}
                            className="w-6 h-6 rounded-full object-cover shrink-0 border border-[#0B7CF2]/40 shadow-xs"
                            loading="lazy"
                            decoding="async"
                          />
                          <span className="text-[12px] font-bold text-[#071E61] truncate">
                            Marjohn Robillo
                          </span>
                          <span className="text-[10px] text-[#0B7CF2] bg-[#EDF7FF] border border-[#0B7CF2]/15 px-1.5 py-0.5 rounded font-medium shrink-0">
                            Systems Architect
                          </span>
                        </div>

                        {/* Post Title */}
                        <Link to={`/blog/${post.slug}`}>
                          <h2 className="text-[18px] sm:text-[20px] font-heading font-extrabold text-[#071E61] group-hover:text-[#0B7CF2] transition-colors line-clamp-2 leading-tight mb-2.5">
                            {post.title}
                          </h2>
                        </Link>

                        {/* Excerpt */}
                        <p className="text-xs sm:text-[13px] text-[#536B89] leading-relaxed line-clamp-2 mb-4">
                          {post.excerpt}
                        </p>

                        {/* Inline FAQ preview items if available */}
                        {faqs.length > 0 && (
                          <div className="mt-auto mb-4 space-y-1.5 pt-2 border-t border-slate-100">
                            <div className="flex items-center gap-1 text-[10px] font-bold text-[#0B7CF2] uppercase tracking-wider mb-1">
                              <span>Quick FAQs</span>
                            </div>
                            {faqs.map((faq, idx) => (
                              <CardFaqAccordion key={idx} faq={faq} />
                            ))}
                          </div>
                        )}

                        {/* Bottom Read Article Link */}
                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs font-bold text-[#071E61] group-hover:text-[#0B7CF2] transition-colors mt-auto">
                          <Link
                            to={`/blog/${post.slug}`}
                            className="inline-flex items-center gap-1.5"
                          >
                            <span>Read article</span>
                            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-12 pt-6 border-t border-slate-200">
                <button
                  onClick={() => {
                    setCurrentPage((p) => Math.max(1, p - 1));
                    window.scrollTo({ top: 320, behavior: "smooth" });
                  }}
                  disabled={currentPage === 1}
                  className="p-2.5 bg-white border border-slate-200 rounded-lg text-[#071E61] hover:bg-[#F0F8FF] hover:border-[#0B7CF2]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                  aria-label="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-1.5">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => {
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => {
                              setCurrentPage(page);
                              window.scrollTo({ top: 320, behavior: "smooth" });
                            }}
                            className={`min-w-9 h-9 px-2 rounded-lg text-xs font-bold transition-all ${
                              currentPage === page
                                ? "bg-[#062447] text-white shadow-sm"
                                : "bg-white border border-slate-200 text-[#536B89] hover:bg-[#F0F8FF] hover:text-[#071E61]"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span
                            key={page}
                            className="px-1 text-slate-400 text-xs"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    },
                  )}
                </div>

                <button
                  onClick={() => {
                    setCurrentPage((p) => Math.min(totalPages, p + 1));
                    window.scrollTo({ top: 320, behavior: "smooth" });
                  }}
                  disabled={currentPage === totalPages}
                  className="p-2.5 bg-white border border-slate-200 rounded-lg text-[#071E61] hover:bg-[#F0F8FF] hover:border-[#0B7CF2]/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                  aria-label="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* =========================================================
              RIGHT SIDEBAR: Exactly matches the reference image
              1. Top Ranking Articles Panel
              2. "You're losing 20+ hours a week" CTA card
              3. "Dedicated VA / Architect" Special Offer card
              ========================================================= */}
          <aside className="space-y-6 lg:sticky lg:top-28">
            {/* Top Ranking Articles Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_8px_24px_rgba(8,42,102,0.06)]">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-slate-100">
                <span className="w-7 h-7 rounded-full bg-[#EAF5FF] text-[#0B7CF2] flex items-center justify-center">
                  <TrendingUp className="w-4 h-4" />
                </span>
                <h3 className="font-heading font-black text-[16px] text-[#071E61]">
                  Top Ranking Articles
                </h3>
              </div>

              <div className="divide-y divide-slate-100">
                {topRankingArticles.map((article, idx) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="group py-3.5 first:pt-0 last:pb-0 flex items-start gap-3 transition-colors block"
                  >
                    <span className="font-heading font-black text-sm text-[#0B7CF2]/50 group-hover:text-[#0B7CF2] transition-colors shrink-0 w-6 pt-0.5">
                      {String(idx + 1).padStart(2, "0")}
                    </span>

                    <div className="min-w-0">
                      <span className="text-[10px] font-bold tracking-wider uppercase text-[#0B7CF2] block mb-1">
                        {article.category}
                      </span>
                      <h4 className="text-[13px] font-bold text-[#071E61] group-hover:text-[#0B7CF2] transition-colors leading-snug line-clamp-2">
                        {article.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-[#6A7D97]">
                        <span>{article.readTime}</span>
                        <span>•</span>
                        <span className="text-[#0B7CF2] font-medium">
                          Top read
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Dark Green / Teal Systems Audit CTA Card */}
            <div className="rounded-2xl p-6 bg-gradient-to-br from-[#06313B] to-[#0A4854] text-white shadow-[0_16px_36px_rgba(6,49,59,0.25)] relative overflow-hidden">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[#35F35A] text-[10px] font-bold tracking-wider uppercase mb-3 border border-white/10">
                <Sparkles className="w-3 h-3" />
                <span>FREE • 15 MIN</span>
              </div>

              <h3 className="text-xl font-heading font-extrabold text-white leading-tight mb-2">
                You're losing 20+ hours a week
              </h3>
              <p className="text-xs text-white/80 leading-relaxed mb-4">
                Get a tailored delegation audit and we'll pinpoint the exact
                tasks you can hand off today.
              </p>

              {/* 3 Metric Pills */}
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="bg-white/10 rounded-lg p-2 border border-white/10">
                  <span className="block text-base font-black text-white">
                    20+
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-white/70">
                    HRS SAVED/WK
                  </span>
                </div>
                <div className="bg-white/10 rounded-lg p-2 border border-white/10">
                  <span className="block text-base font-black text-white">
                    15
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-white/70">
                    MIN CALL
                  </span>
                </div>
                <div className="bg-white/10 rounded-lg p-2 border border-white/10">
                  <span className="block text-base font-black text-white">
                    100%
                  </span>
                  <span className="text-[9px] uppercase tracking-wider text-white/70">
                    FREE
                  </span>
                </div>
              </div>

              {/* Action Form */}
              <form onSubmit={handleAuditSubmit} className="space-y-2">
                <input
                  type="email"
                  placeholder="Enter work email..."
                  value={auditEmail}
                  onChange={(e) => setAuditEmail(e.target.value)}
                  className="w-full h-10 px-3.5 bg-white/15 border border-white/20 rounded-lg text-xs text-white placeholder:text-white/60 focus:outline-none focus:bg-white/25 transition-all"
                  required
                />
                <button
                  type="submit"
                  className="w-full h-11 bg-white hover:bg-slate-100 text-[#06313B] font-bold text-xs rounded-lg shadow-md transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
                >
                  <span>Get My Systems Audit</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
              <p className="text-[10px] text-white/60 text-center mt-2.5">
                No card required • Joined by 300+ agency founders
              </p>
            </div>

            {/* Consultation / Discovery Call Offer Card (Teal/Emerald Gradient) */}
            <div className="rounded-2xl p-6 bg-gradient-to-br from-[#0B5145] to-[#147464] text-white shadow-[0_16px_36px_rgba(11,81,69,0.22)] relative overflow-hidden">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#FFB800] text-[#071E61] text-[10px] font-black uppercase tracking-wider mb-3">
                <Flame className="w-3 h-3 fill-current" />
                <span>FREE CONSULTATION</span>
              </div>

              <h3 className="text-lg font-heading font-black text-white leading-tight mb-2">
                Talk to an Expert Systems Architect
              </h3>

              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-heading font-black text-[#35F35A]">
                  FREE
                </span>
                <span className="text-[11px] text-white/80 font-medium">
                  instead of paying costly consultation fees
                </span>
              </div>

              <p className="text-xs text-white/85 leading-relaxed mb-4">
                Instead of paying expensive consultation fees, book a 1-on-1
                strategy call with an expert to audit your revenue systems —
                100% free.
              </p>

              <ul className="space-y-2 mb-5 text-xs text-white/90">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#35F35A] shrink-0" />
                  <span>Direct diagnostic of your current tool stack</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#35F35A] shrink-0" />
                  <span>Actionable blueprint you can keep and deploy</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#35F35A] shrink-0" />
                  <span>Zero pitch, zero commitment required</span>
                </li>
              </ul>

              <a
                href="/#booking"
                className="w-full h-11 bg-[#FFD600] hover:bg-[#FFBF00] text-[#071E61] font-black text-xs rounded-lg shadow-md transition-transform hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
              >
                <span>Book Free Strategy Call</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
